/** CSS contract: css-library/components/01-leaves/paragraph.css. */
import type { Ref } from 'react';
import type { ElementType, HTMLAttributes, LabelHTMLAttributes } from 'react';
import { cx } from '../lib/cx';

export type ParagraphVariant = 'body' | 'lead' | 'muted';

export interface ParagraphProps extends HTMLAttributes<HTMLElement> {
  variant?: ParagraphVariant;
  as?: 'p' | 'span' | 'div' | 'small';
}

export function Paragraph(
  { ref, variant = 'body', as = 'p', className, children, ...rest }: ParagraphProps & { ref?: Ref<HTMLElement> },
) {
  const Tag = as as ElementType;
  return (
    <Tag ref={ref} className={cx('paragraph', variant !== 'body' && `paragraph--${variant}`, className)} {...rest}>
      {children}
    </Tag>
  );
}

const RTL_SCRIPT = ['Arabic', 'Hebrew', 'Syriac', 'Thaana', 'Nko'].map((s) => `\\p{Script_Extensions=${s}}`).join('');
const RTL = new RegExp(`[${RTL_SCRIPT}]`, 'u');

const LTR_RUN = new RegExp(`((?:[+§]\\s?)?[\\p{Script=Latin}0-9](?:[^${RTL_SCRIPT}\\n()[\\]]*[\\p{Script=Latin}0-9])?)`, 'u');

export interface BidiTextProps {
  text: string;
}

export function BidiText({ text }: BidiTextProps) {
  if (!RTL.test(text)) return <>{text}</>;
  const parts = text.split(LTR_RUN);
  return <>{parts.map((part, i) => (i % 2 === 1 ? <bdi key={i} dir="ltr">{part}</bdi> : part))}</>;
}

export interface ProseProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  paragraphs: readonly string[];
}

export function Prose(
  { ref, paragraphs, className, ...rest }: ProseProps & { ref?: Ref<HTMLDivElement> },
) {
  return (
    <div ref={ref} className={cx('prose', className)} {...rest}>
      {paragraphs.map((paragraph, i) => (
        <p key={i}>
          <BidiText text={paragraph} />
        </p>
      ))}
    </div>
  );
}

export interface VisuallyHiddenProps extends LabelHTMLAttributes<HTMLElement> {
  as?: 'span' | 'label' | 'div';
}

export function VisuallyHidden(
  { ref, as = 'span', className, children, ...rest }: VisuallyHiddenProps & { ref?: Ref<HTMLElement> },
) {
  const Tag = as as ElementType;
  return (
    <Tag ref={ref} className={cx('visually-hidden', className)} {...rest}>
      {children}
    </Tag>
  );
}
